import { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  List,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import HistoryIcon from '@mui/icons-material/History';
import { managerApi } from '../../api/manager_api';
import { useManagerUser } from './ManagerUserPage';
import { RequestListItem } from '../../components/RequestListItem';
import type { FHRequest } from '../../types';

export const ManagerUserAssistant = observer(() => {
  const { userKey } = useParams<{ userKey: string }>();
  const navigate = useNavigate();
  const { user, error: userError } = useManagerUser();

  const [requests, setRequests] = useState<FHRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchRequests = async (loadMore = false) => {
    if (!user?.key) return;

    if (loadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const result = await managerApi.getAssistantRequests(
        user.key,
        loadMore ? cursor || undefined : undefined
      );

      if (loadMore) {
        setRequests((prev) => [...prev, ...result.items]);
      } else {
        setRequests(result.items);
      }
      setCursor(result.cursor);
      setHasMore(!!result.cursor);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load requests');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, [user?.key]);

  const handleRequestClick = (requestKey: string) => {
    navigate(`/assistant/request/${requestKey}`);
  };

  if (!user) {
    return null;
  }

  return (
    <>
      {(error || userError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || userError}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Assistant Actions
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<PaymentIcon />}
              onClick={() => navigate(`/assistant/payments?user=${userKey}`)}
            >
              View Payouts
            </Button>
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={() => navigate(`/manager/user/${userKey}/closed`)}
            >
              View Closed Tasks
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Request History
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            All requests claimed by this assistant
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : requests.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No requests found
            </Typography>
          ) : (
            <>
              <List disablePadding>
                {requests.map((request, index) => (
                  <RequestListItem
                    key={request.key}
                    request={request}
                    variant="claimed"
                    showDivider={index > 0}
                    onClick={handleRequestClick}
                  />
                ))}
              </List>

              {hasMore && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    onClick={() => fetchRequests(true)}
                    disabled={loadingMore}
                  >
                    {loadingMore ? <CircularProgress size={20} /> : 'Load More'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </>
  );
});
